import { createTheme, PaletteOptions, Theme } from '@mui/material';
import {
	ParsedDescopeTheme,
	ParsedDescopeThemeVars
} from 'modules/theme/theme';
import { PortalContainerStyle, PortalStylesConfig } from 'types/theme';
import { colorHexValues } from './colorHexValues';

const resolveColors = (value: string) => {
	if (
		!value.match('^#') &&
		!value.match('^rgb|rgba|hsl|hsla|color') &&
		colorHexValues[value]
	) {
		return colorHexValues[value];
	}
	return value;
};

const stripCssUrl = (value?: string): string | undefined => {
	if (!value) {
		return undefined;
	}
	const match = value.match(/^url\((['"]?)(.*?)\1\)$/);
	return match ? match[2] : value;
};

const UNSET = new Set(['', 'unset', 'none', 'initial']);

// Region props arrive already var()-resolved (parseDescopeTheme runs resolveCssVars
// over the whole globals map first), so each value is a literal length/color or a
// known color name. Drop unset values; map color names to hex via resolveColors.
const buildContainer = (
	vars: Record<string, string | undefined>,
	region: string
): PortalContainerStyle | undefined => {
	const val = (prop: string): string | undefined => {
		const raw = vars[`--descope-admin-portal-${region}-${prop}`]?.trim();
		return raw && !UNSET.has(raw) ? raw : undefined;
	};
	const style: PortalContainerStyle = {};

	const bg = val('background-color');
	if (bg) {
		style.backgroundColor = resolveColors(bg);
	}
	const bgImage = val('background-image');
	if (bgImage) {
		style.backgroundImage = bgImage.startsWith('url(')
			? bgImage
			: `url(${bgImage})`;
	}
	const borderStyle = val('border-style');
	if (borderStyle) {
		const width = val('border-width') ?? '1px';
		const borderColor = val('border-color');
		const color = borderColor ? resolveColors(borderColor) : 'currentColor';
		style.border = `${width} ${borderStyle} ${color}`;
	}
	return Object.keys(style).length ? style : undefined;
};

const buildPortalStyles = (
	parsedVars: ParsedDescopeThemeVars,
	loaderUrl?: string
): PortalStylesConfig | undefined => {
	const vars = parsedVars as Record<string, string | undefined>;
	const config: PortalStylesConfig = {
		background: buildContainer(vars, 'background'),
		header: buildContainer(vars, 'header'),
		sidebar: buildContainer(vars, 'side'),
		content: buildContainer(vars, 'main')
	};

	const loader = stripCssUrl(loaderUrl);
	if (loader) {
		config.loader = { url: loader };
	}

	const hasAny = Object.values(config).some((v) => v !== undefined);
	return hasAny ? config : undefined;
};

const createMuiThemeMode = (
	mode: 'light' | 'dark',
	theme: ParsedDescopeTheme['light'] | ParsedDescopeTheme['dark'] | undefined
): Theme => {
	if (!theme) {
		return createTheme({ palette: { mode } });
	}

	const paletteOptions: PaletteOptions = {
		mode
	};

	const vars = theme.globalsVars;
	const { logoUrl, faviconUrl, loaderUrl } = theme;

	if (vars['--descope-colors-primary-main']) {
		paletteOptions.primary = {
			main: resolveColors(vars['--descope-colors-primary-main']),
			dark: resolveColors(vars['--descope-colors-primary-dark']),
			light: resolveColors(vars['--descope-colors-primary-light']),
			highlight: resolveColors(vars['--descope-colors-primary-highlight']),
			contrastText: resolveColors(vars['--descope-colors-primary-contrast'])
		};
	}

	if (vars['--descope-colors-secondary-main']) {
		paletteOptions.secondary = {
			main: resolveColors(vars['--descope-colors-secondary-main']),
			dark: resolveColors(vars['--descope-colors-secondary-dark']),
			light: resolveColors(vars['--descope-colors-secondary-light']),
			highlight: resolveColors(vars['--descope-colors-secondary-highlight']),
			contrastText: resolveColors(vars['--descope-colors-secondary-contrast'])
		};
	}

	if (vars['--descope-colors-warning-main']) {
		paletteOptions.warning = {
			main: resolveColors(vars['--descope-colors-warning-main']),
			dark: resolveColors(vars['--descope-colors-warning-dark']),
			light: resolveColors(vars['--descope-colors-warning-light']),
			highlight: resolveColors(vars['--descope-colors-warning-highlight']),
			contrastText: resolveColors(vars['--descope-colors-warning-contrast'])
		};
	}

	if (vars['--descope-colors-error-main']) {
		paletteOptions.error = {
			main: resolveColors(vars['--descope-colors-error-main']),
			dark: resolveColors(vars['--descope-colors-error-dark']),
			light: resolveColors(vars['--descope-colors-error-light']),
			highlight: resolveColors(vars['--descope-colors-error-highlight']),
			contrastText: resolveColors(vars['--descope-colors-error-contrast'])
		};
	}

	if (vars['--descope-colors-success-main']) {
		paletteOptions.success = {
			main: resolveColors(vars['--descope-colors-success-main']),
			dark: resolveColors(vars['--descope-colors-success-dark']),
			light: resolveColors(vars['--descope-colors-success-light']),
			highlight: resolveColors(vars['--descope-colors-success-highlight']),
			contrastText: resolveColors(vars['--descope-colors-success-contrast'])
		};
	}

	if (vars['--descope-colors-surface-main']) {
		paletteOptions.grey = {
			main: resolveColors(vars['--descope-colors-surface-main']),
			dark: resolveColors(vars['--descope-colors-surface-dark']),
			light: resolveColors(vars['--descope-colors-surface-light']),
			highlight: resolveColors(vars['--descope-colors-surface-highlight']),
			contrastText: resolveColors(vars['--descope-colors-surface-contrast']),
			'50': resolveColors(vars['--descope-colors-surface-main']),
			'100': resolveColors(vars['--descope-colors-surface-highlight']),
			'200': resolveColors(vars['--descope-colors-surface-highlight']),
			'300': resolveColors(vars['--descope-colors-surface-light']),
			'400': resolveColors(vars['--descope-colors-surface-light']),
			'500': resolveColors(vars['--descope-colors-surface-light']),
			'600': resolveColors(vars['--descope-colors-surface-dark']),
			'700': resolveColors(vars['--descope-colors-surface-dark']),
			'800': resolveColors(vars['--descope-colors-surface-dark']),
			'900': resolveColors(vars['--descope-colors-surface-contrast']),
			A100: resolveColors(vars['--descope-colors-surface-contrast']),
			A200: resolveColors(vars['--descope-colors-surface-contrast']),
			A400: resolveColors(vars['--descope-colors-surface-contrast']),
			A700: resolveColors(vars['--descope-colors-surface-contrast'])
		};
	}

	return createTheme({
		assets: {
			favicon: faviconUrl,
			logo: logoUrl
		},
		portalStyles: buildPortalStyles(vars, loaderUrl),
		palette: paletteOptions,
		typography: {
			h1: {
				fontFamily: vars['--descope-typography-h1-font'],
				fontWeight: vars['--descope-typography-h1-weight'],
				fontSize: vars['--descope-typography-h1-size']
			},
			h2: {
				fontFamily: vars['--descope-typography-h2-font'],
				fontWeight: vars['--descope-typography-h2-weight'],
				fontSize: vars['--descope-typography-h2-size']
			},
			h3: {
				fontFamily: vars['--descope-typography-h3-font'],
				fontWeight: vars['--descope-typography-h3-weight'],
				fontSize: vars['--descope-typography-h3-size']
			},
			h4: {
				fontFamily: vars['--descope-typography-h3-font']
			},
			h5: {
				fontFamily: vars['--descope-typography-h3-font']
			},
			h6: {
				fontFamily: vars['--descope-typography-h3-font']
			},
			subtitle1: {
				fontFamily: vars['--descope-typography-subtitle1-font'],
				fontWeight: vars['--descope-typography-subtitle1-weight'],
				fontSize: vars['--descope-typography-subtitle1-size']
			},
			subtitle2: {
				fontFamily: vars['--descope-typography-subtitle2-font'],
				fontWeight: vars['--descope-typography-subtitle2-weight'],
				fontSize: vars['--descope-typography-subtitle2-size']
			},
			body1: {
				fontFamily: vars['--descope-typography-body1-font'],
				fontWeight: vars['--descope-typography-body1-weight'],
				fontSize: vars['--descope-typography-body1-size']
			},
			body2: {
				fontFamily: vars['--descope-typography-body2-font'],
				fontWeight: vars['--descope-typography-body2-weight'],
				fontSize: vars['--descope-typography-body2-size']
			},
			button: {
				textTransform: 'none'
			}
		},
		components: {
			MuiPaper: {
				defaultProps: {
					elevation: 0
				},
				styleOverrides: {
					root: {
						backgroundColor: vars['--descope-colors-surface-main'],
						borderRadius: vars['--descope-radius-md']
					}
				}
			},
			MuiAppBar: {
				styleOverrides: {
					root: {
						backgroundColor: vars['--descope-colors-surface-main'],
						borderRadius: 0
					}
				}
			}
		}
	});
};

export const createMuiTheme = (theme: ParsedDescopeTheme | undefined) => ({
	light: createMuiThemeMode('light', theme?.light),
	dark: createMuiThemeMode('dark', theme?.dark)
});
